
import React from 'react';
import { 
  BarChart as RechartsBarChart, 
  Bar, 
  LineChart as RechartsLineChart,
  Line,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  Legend
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';

interface MonthlyData {
  month: string;
  clicks: number;
}

interface TopPost {
  id: string;
  title: string;
  slug: string;
  view_count: number;
}

interface AnalyticsChartsProps {
  monthlyData: MonthlyData[];
  topPosts: TopPost[];
}

const AnalyticsCharts: React.FC<AnalyticsChartsProps> = ({ monthlyData = [], topPosts = [] }) => {
  // Ensure we have valid arrays even if undefined is passed
  const safeMonthlyData = monthlyData || [];
  const safeTopPosts = topPosts || [];
  
  const formatMonth = (monthStr: string) => {
    if (!monthStr) return '';
    
    try {
      const [year, month] = monthStr.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleDateString('en-US', { month: 'short' });
    } catch (e) {
      console.error('Error formatting month:', e);
      return monthStr;
    }
  };

  // Format data for top posts chart - safely
  const topPostsChartData = safeTopPosts.map(post => ({
    name: post.title ? (post.title.length > 30 ? post.title.substring(0, 30) + '...' : post.title) : 'Untitled',
    views: post.view_count || 0
  })).sort((a, b) => (b.views || 0) - (a.views || 0));

  // Format data for monthly chart - safely
  const formattedMonthlyData = safeMonthlyData.map(item => ({
    name: formatMonth(item.month || ''),
    clicks: item.clicks || 0
  }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
      <Card>
        <CardHeader>
          <CardTitle>Monthly CTA Clicks</CardTitle>
        </CardHeader>
        <CardContent className="h-80">
          <ChartContainer
            className="h-full"
            config={{
              clicks: {
                label: 'CTA Clicks',
                theme: {
                  light: '#10b981',
                  dark: '#10b981'
                }
              },
            }}
          >
            <RechartsLineChart data={formattedMonthlyData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <ChartTooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="rounded-lg border bg-background p-2 shadow-sm">
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex flex-col">
                            <span className="text-[0.70rem] uppercase text-muted-foreground">
                              Month
                            </span>
                            <span className="font-bold text-muted-foreground">
                              {payload[0].payload.name}
                            </span>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-[0.70rem] uppercase text-muted-foreground">
                              Clicks
                            </span>
                            <span className="font-bold">
                              {payload[0].value}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Line 
                type="monotone" 
                dataKey="clicks" 
                stroke="var(--color-clicks)" 
                activeDot={{ r: 8 }} 
              />
            </RechartsLineChart>
          </ChartContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Top Posts by Views</CardTitle>
        </CardHeader>
        <CardContent className="h-80">
          <ChartContainer
            className="h-full"
            config={{
              views: {
                label: 'Views',
                theme: {
                  light: '#06b6d4',
                  dark: '#06b6d4'
                }
              },
            }}
          >
            <RechartsBarChart data={topPostsChartData} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 40 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={150} />
              <ChartTooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="rounded-lg border bg-background p-2 shadow-sm">
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex flex-col">
                            <span className="text-[0.70rem] uppercase text-muted-foreground">
                              Post
                            </span>
                            <span className="font-bold text-muted-foreground">
                              {payload[0].payload.name}
                            </span>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-[0.70rem] uppercase text-muted-foreground">
                              Views
                            </span>
                            <span className="font-bold">
                              {payload[0].value}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Bar dataKey="views" fill="var(--color-views)" radius={[4, 4, 0, 0]} />
            </RechartsBarChart>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsCharts;
